<script lang="ts">
  import { layoutPanel } from '$lib/stores/layout-panel';
  import { AUTH_DRAWER_IDS, CART_DRAWER_ID } from '$lib/constants';
  import { drawer } from '$lib/stores/drawer';
  import { progressBarConfig } from '$lib/stores/campaigns';
  import { aboveLg } from '$lib/stores/breakpoints';
  import { createBreakpointMediaQueryStore } from '$lib/stores/media';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import { t } from '$lib/i18n';
  import { fade } from 'svelte/transition';
  import { user } from '$lib/stores/user';

  import Logo from '$components/logo.svelte';
  import Icon from '$components/ui/icon/icon.svelte';
  import Link from '$components/ui/link.svelte';
  import Cart from '$components/cart/cart.svelte';
  import ProgressBar from '$components/progress-bar.svelte';

  export let homeUrl: string;
  export let wishlistUrl: string;
  export let accountUrl: string;
  export let showProgress = false;

  const lg = createBreakpointMediaQueryStore('lg');
</script>

<div class="container mx-auto hidden lg:flex h-12 items-center justify-between lg:h-14">
  <div class="flex basis-full gap-8">
    <!-- Home -->
    <a
      href={homeUrl}
      aria-label="Go to homepage"
      class="rounded-micro border-none shadow-none outline-none transition-shadow focus-visible:ring motion-reduce:transition-none"
    >
      <Logo aria-hidden="true" />
    </a>

    <Link
      as="button"
      class="hidden lg:inline-flex text-md"
      aria-label={$layoutPanel === 'menu' ? $t('a11y.close-nav-menu') : $t('a11y.open-nav-menu')}
      on:keydown={(e) => {
        if (!['Enter', 'Space'].includes(e.code)) return;

        e.preventDefault();
        layoutPanel.toggle('menu');
      }}
      on:mouseenter={() => layoutPanel.open('menu')}
      on:mouseleave={() => layoutPanel.close({ timeout: 600 })}
      on:touchstart={() => layoutPanel.toggle('menu')}
    >
      {$t('topnav.menu-cta')}
    </Link>
  </div>

  {#if $lg && $progressBarConfig && !$progressBarConfig.hideSaleComponents && showProgress}
    <div
      transition:fade={{ duration: 150, delay: 300 }}
      class="bg-highlight/64 h-9 w-full max-w-md mx-2 rounded-md p-1.5 shrink-0"
    >
      <ProgressBar on:click={() => drawer.open(CART_DRAWER_ID)} showConfettiAnimation={true} />
    </div>
  {/if}

  <!-- Icons in right part of navbar -->
  <div class="flex basis-full justify-end items-center lg:gap-5 lg:mr-0 -mr-3">
    {#if !$layoutPanel || $aboveLg}
      <!-- Search -->
      {#if !$drawer.id || $lg}
        <button
          transition:fade|global={{ duration: $reducedMotion ? 0 : 150 }}
          class="flex rounded-micro border-none shadow-none outline-none transition-shadow focus-visible:ring h-12 items-center justify-center aspect-square lg:h-fit"
          aria-label={$layoutPanel === 'search' ? 'Close search menu' : 'Open search menu'}
          on:click={() => layoutPanel.toggle('search')}
        >
          <Icon size="lg" icon="search" />
        </button>
      {/if}

      {#if !$drawer.id || $lg}
        <!-- Wishlist -->
        <a
          transition:fade|global={{ duration: $reducedMotion ? 0 : 150 }}
          href={wishlistUrl}
          aria-label="Go to wishlist"
          class="hidden lg:flex rounded-micro border-none shadow-none outline-none transition-shadow focus-visible:ring h-12 items-center justify-center aspect-square lg:h-fit"
        >
          <Icon size="lg" icon="heart-empty" />
        </a>
      {/if}

      <Cart />

      <!-- Account -->
      <svelte:element
        this={$user ? 'a' : 'button'}
        transition:fade|global={{ duration: $reducedMotion ? 0 : 150 }}
        role={$user ? 'link' : undefined}
        href={$user ? accountUrl : undefined}
        on:click={$user ? undefined : () => drawer.open(AUTH_DRAWER_IDS.LOGIN)}
        class="hidden lg:flex rounded-micro border-none shadow-none outline-none transition-shadow focus-visible:ring h-12 items-center justify-center aspect-square lg:h-fit"
      >
        {#if !$user}
          <p class="text-md">{$t('topnav.signin')}</p>
        {:else}
          <Icon size="lg" icon="user" />
        {/if}
      </svelte:element>
    {/if}

    <!-- Nav Menu -->
    {#if !$drawer.id && !$layoutPanel}
      <button
        name={$layoutPanel === 'menu' ? $t('a11y.close-nav-menu') : $t('a11y.open-nav-menu')}
        class={'flex rounded-micro border-none shadow-none outline-none transition-shadow focus-visible:ring motion-reduce:transition-none h-12 items-center justify-center aspect-square lg:h-fit lg:hidden'}
        aria-label={$layoutPanel === 'menu' ? $t('a11y.close-nav-menu') : $t('a11y.open-nav-menu')}
        on:click={() => layoutPanel.toggle('menu')}
      >
        <Icon size="lg" icon={$layoutPanel === 'menu' ? 'cross' : 'menu'} />
      </button>
    {/if}

    <!-- Mobile close drawer button -->
    {#if $drawer.id || !!$layoutPanel}
      <button
        name="Close drawer"
        class="flex rounded-micro border-none shadow-none outline-none transition-shadow focus-visible:ring motion-reduce:transition-none h-12 items-center justify-center aspect-square lg:h-fit lg:hidden"
        aria-label="Close drawer"
        on:click={() => {
          drawer.close();
          layoutPanel.close();
        }}
      >
        <Icon size="lg" icon="cross" />
      </button>
    {/if}
  </div>
</div>
